import { useEffect, useRef } from "react";
import * as THREE from "three";
import type { Country, CountryId } from "../data";

type GlobeProps = {
  countries: Country[];
  selectedId: CountryId;
  onSelect: (id: CountryId) => void;
  onHover: (id: CountryId | null) => void;
};

function seededRandom(seed: number) {
  let value = seed;
  return () => {
    value = (value * 9301 + 49297) % 233280;
    return value / 233280;
  };
}

function latLonToVector3(lat: number, lon: number, radius: number) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -(radius * Math.sin(phi) * Math.cos(theta)),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta),
  );
}

function createArc(start: THREE.Vector3, end: THREE.Vector3, height = 0.7) {
  const midpoint = start.clone().add(end).multiplyScalar(0.5).normalize().multiplyScalar(start.length() + height);
  const curve = new THREE.QuadraticBezierCurve3(start, midpoint, end);
  return curve.getPoints(44);
}

export default function Globe({ countries, selectedId, onSelect, onHover }: GlobeProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const onSelectRef = useRef(onSelect);
  const onHoverRef = useRef(onHover);

  onSelectRef.current = onSelect;
  onHoverRef.current = onHover;

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.set(0, 0.25, 6.6);

    const group = new THREE.Group();
    group.rotation.set(-0.18, -0.42, 0);
    scene.add(group);

    const glow = new THREE.Mesh(
      new THREE.SphereGeometry(2.78, 64, 64),
      new THREE.MeshBasicMaterial({ color: 0x0e6b68, transparent: true, opacity: 0.07, side: THREE.BackSide }),
    );
    group.add(glow);

    const globe = new THREE.Mesh(
      new THREE.SphereGeometry(2.48, 72, 72),
      new THREE.MeshBasicMaterial({ color: 0x092324, transparent: true, opacity: 0.88 }),
    );
    group.add(globe);

    const wire = new THREE.LineSegments(
      new THREE.WireframeGeometry(new THREE.SphereGeometry(2.5, 28, 18)),
      new THREE.LineBasicMaterial({ color: 0x2f8f88, transparent: true, opacity: 0.19 }),
    );
    group.add(wire);

    const random = seededRandom(42);
    const pointPositions: number[] = [];
    const pointColors: number[] = [];
    for (let i = 0; i < 1450; i += 1) {
      const u = random();
      const v = random();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      const radius = 2.515 + random() * 0.018;
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.cos(phi);
      const z = radius * Math.sin(phi) * Math.sin(theta);
      pointPositions.push(x, y, z);
      const c = random() > 0.78 ? new THREE.Color(0xc7a45a) : new THREE.Color(0x56d5c7);
      pointColors.push(c.r, c.g, c.b);
    }
    const pointsGeometry = new THREE.BufferGeometry();
    pointsGeometry.setAttribute("position", new THREE.Float32BufferAttribute(pointPositions, 3));
    pointsGeometry.setAttribute("color", new THREE.Float32BufferAttribute(pointColors, 3));
    const points = new THREE.Points(
      pointsGeometry,
      new THREE.PointsMaterial({ size: 0.016, vertexColors: true, transparent: true, opacity: 0.86 }),
    );
    group.add(points);

    const addRoute = (a: Country, b: Country, emphasis: boolean) => {
      const start = latLonToVector3(a.lat, a.lon, 2.58);
      const end = latLonToVector3(b.lat, b.lon, 2.58);
      const geometry = new THREE.BufferGeometry().setFromPoints(createArc(start, end, emphasis ? 0.92 : 0.62));
      const material = new THREE.LineBasicMaterial({
        color: emphasis ? 0xc7a45a : 0x35c8bf,
        transparent: true,
        opacity: emphasis ? 0.84 : 0.48,
      });
      group.add(new THREE.Line(geometry, material));
    };

    const uganda = countries.find((country) => country.id === "uganda");
    const mozambique = countries.find((country) => country.id === "mozambique");
    const activeCountry = countries.find((country) => country.id === selectedId) ?? countries[0];
    countries.forEach((country) => {
      if (country.id !== selectedId) addRoute(activeCountry, country, country.role === "Pilot country");
    });
    if (uganda && mozambique) addRoute(uganda, mozambique, true);

    const markerObjects: THREE.Mesh[] = [];
    countries.forEach((country) => {
      const selected = country.id === selectedId;
      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(selected ? 0.11 : 0.074, 24, 24),
        new THREE.MeshBasicMaterial({ color: selected ? 0xffffff : country.role === "Pilot country" ? 0xc7a45a : 0x49d6c8 }),
      );
      marker.position.copy(latLonToVector3(country.lat, country.lon, 2.68));
      marker.userData.countryId = country.id;
      markerObjects.push(marker);
      group.add(marker);

      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(selected ? 0.18 : 0.128, 24, 24),
        new THREE.MeshBasicMaterial({
          color: country.role === "Pilot country" ? 0xc7a45a : 0x49d6c8,
          transparent: true,
          opacity: selected ? 0.26 : 0.14,
        }),
      );
      halo.position.copy(marker.position);
      group.add(halo);
    });

    const lightA = new THREE.PointLight(0xffffff, 1.4, 18);
    lightA.position.set(1.8, 2.4, 3.2);
    scene.add(lightA);

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let hovered: CountryId | null = null;
    let frame = 0;
    let disposed = false;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      renderer.setSize(rect.width, rect.height, false);
      camera.aspect = rect.width / Math.max(rect.height, 1);
      camera.updateProjectionMatrix();
    };

    const updatePointer = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObjects(markerObjects, false)[0];
      const next = (hit?.object.userData.countryId as CountryId | undefined) ?? null;
      if (next !== hovered) {
        hovered = next;
        canvas.style.cursor = next ? "pointer" : "default";
        onHoverRef.current(next);
      }
    };

    const click = () => {
      if (hovered) onSelectRef.current(hovered);
    };

    const animate = () => {
      if (disposed) return;
      if (!prefersReducedMotion) {
        group.rotation.y += 0.0017;
        points.rotation.y -= 0.0009;
      }
      renderer.render(scene, camera);
      frame = window.requestAnimationFrame(animate);
    };

    resize();
    animate();
    window.addEventListener("resize", resize);
    canvas.addEventListener("pointermove", updatePointer);
    canvas.addEventListener("pointerleave", () => {
      hovered = null;
      canvas.style.cursor = "default";
      onHoverRef.current(null);
    });
    canvas.addEventListener("click", click);

    return () => {
      disposed = true;
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("pointermove", updatePointer);
      canvas.removeEventListener("click", click);
      renderer.dispose();
      scene.traverse((object) => {
        const mesh = object as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          const material = mesh.material;
          if (Array.isArray(material)) material.forEach((item) => item.dispose());
          else material.dispose();
        }
      });
    };
  }, [countries, selectedId]);

  return (
    <div className="globe-stage" aria-label="Interactive country network globe">
      <canvas ref={canvasRef} className="globe-canvas" />
    </div>
  );
}
